import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { useFeatureFlag } from "@jsq/flags-react";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { toGlobalId } from "#utils/graphql_utils";
import { CountrySelectionDropDown } from "../../../../common/components/CountrySelectionDropDown";
import { SimpleFormRow } from "../../../../common/components/Form/Layout/SimpleFormRow";
import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { SharedAddressInput } from "../../../SharedAddressInput";
import { CountrySelectProps } from "../../../SharedAddressInput/types";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { useSetBankCountry } from "../../hooks/use-set-bank-country";
import {
  getCountryValue,
  getIdFromCountry,
  getNameFromCountry,
  mapCountryToValueSafely,
} from "../../mappers";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import {
  CheckPaymentPrefInputFields,
  CheckPaymentPrefInputSchema,
  type CheckPaymentPrefInputType,
} from "./constants";
import { useIsMailingCountryMismatch } from "./use-is-mailing-country-mismatch";
import {
  buildCheckCountryFilter,
  computeAllowedCheckCountryIds,
  isMailingCountryOutsideAllowedRail,
  mapContextStateToFormState,
  mapFormStateToContextState,
} from "./utils";

export const CheckAddressCountrySelect: React.FC<CountrySelectProps> = ({
  name,
  className,
  isFull,
  placeholder,
  handleChange,
  label,
  disabled,
  filterCountries,
}) => {
  return (
    <CountrySelectionDropDown
      name={name}
      className={className}
      isFull={isFull}
      placeholder={placeholder}
      handleChange={handleChange}
      getValue={getCountryValue}
      label={label}
      disabled={disabled}
      filterCountries={filterCountries}
    />
  );
};

export const CheckPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName,
}) => {
  const {
    setPaymentPrefDetail,
    workflowMode,
    selectedBankCountry,
    bankCountryLocked,
    availablePaymentRailsResponse,
  } = useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  const {
    loading: countryLoading,
    error: countryError,
    resolveAndSet,
  } = useSetBankCountry({ paymentPrefDetail });

  // Compute the intersection of allowed Check-rail country IDs once, reuse for both
  // the dropdown filter and the mailing-address checkbox guard (FF-7685).
  const allowedCheckCountryIds = computeAllowedCheckCountryIds(availablePaymentRailsResponse);
  const filterCountries = buildCheckCountryFilter(allowedCheckCountryIds);

  const isInternationalPaymentRailsEnabled = useFeatureFlag("internationalPaymentRails", false);

  useReportError(error ? formatError(error) : null);

  // Lock only when the country was set by an upstream source (country-picker or edit-hydration),
  // not when it was echoed back by this form's own submit (fixes FF-7684).
  const isCountryLocked = isInternationalPaymentRailsEnabled && bankCountryLocked;

  const {
    isMismatch: isMailingCountryMismatch,
    loading: mailingAddressLoading,
    commonMailingCountryObjectId,
    commonMailingCountryObjectName,
  } = useIsMailingCountryMismatch();

  // Disable "Same as mailing address" only when the Check rail actually restricts countries
  // (allowedCheckCountryIds is non-null). When it is null the rail is unrestricted — all
  // countries are allowed — so we must not disable the checkbox even if there is a mismatch
  // between the mailing country and the selected bank country (FF-7685).
  const disableSameAsMailingAddressCheckbox =
    isInternationalPaymentRailsEnabled &&
    allowedCheckCountryIds !== null &&
    (isMailingCountryMismatch ||
      isMailingCountryOutsideAllowedRail(commonMailingCountryObjectId, allowedCheckCountryIds));

  // Resolve and store the account mailing-address country as the bank country in context.
  const resolveMailingCountry = useCallback(() => {
    if (commonMailingCountryObjectId != null) {
      return resolveAndSet({
        countryId: toGlobalId("CountryObjectType", commonMailingCountryObjectId),
      });
    }
    if (commonMailingCountryObjectName) {
      return resolveAndSet({ countryName: commonMailingCountryObjectName });
    }
    return undefined;
  }, [resolveAndSet, commonMailingCountryObjectId, commonMailingCountryObjectName]);

  const handleSameAsMailingAddressChange = useCallback(
    (checked: boolean, { setCountryValue }: { setCountryValue: (value: string) => void }) => {
      if (!checked && selectedBankCountry && isCountryLocked) {
        setCountryValue(
          mapCountryToValueSafely(selectedBankCountry.id, selectedBankCountry.name),
        );
      }
    },
    [selectedBankCountry, isCountryLocked],
  );

  const handleFormSubmission = useCallback(
    (values: CheckPaymentPrefInputType) => {
      if (values.address.sameAsMailingAddress) {
        resolveMailingCountry();
      } else if (values.address.country) {
        const selectedCountryId = getIdFromCountry(values.address.country);
        // In the edit flow selectedBankCountry is already hydrated from the existing pref,
        // so we must also call resolveAndSet when the user picks a *different* country —
        // not just when it is unset (which only covers the create flow).
        const countryChanged = selectedCountryId !== selectedBankCountry?.id;
        if (!selectedBankCountry || countryChanged) {
          resolveAndSet({ countryId: selectedCountryId });
        }
      }
      setPaymentPrefDetail(mapFormStateToContextState(values));
      onContinueClick();
    },
    [
      setPaymentPrefDetail,
      onContinueClick,
      selectedBankCountry,
      resolveAndSet,
      resolveMailingCountry,
    ],
  );

  if (loading || countryLoading || mailingAddressLoading) {
    return <LoadingIndicator />;
  }

  if (error || countryError) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.Check}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "CheckPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
        selectedBankCountry,
      )}
      validationSchema={CheckPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <SimpleFormRow>
        <FormTextInput
          label="Recipient name"
          isFullWidth
          name={CheckPaymentPrefInputFields.PayeeName}
        />
      </SimpleFormRow>
      <SimpleFormRow>
        <FormTextInput
          label="Memo"
          isFullWidth
          name={CheckPaymentPrefInputFields.Memo}
          helpText="Optional"
        />
      </SimpleFormRow>
      <SharedAddressInput
        title="Address"
        sameAsMailingAddressLabel="Same as investor account mailing address"
        CountrySelect={CheckAddressCountrySelect}
        includeSameAsMailingAddressField
        isCountryDisabled={isCountryLocked}
        disableSameAsMailingAddressCheckbox={disableSameAsMailingAddressCheckbox}
        name={CheckPaymentPrefInputFields.Address}
        testIsUSA={(country) => isUSA(getNameFromCountry(country))}
        filterCountries={filterCountries}
        onSameAsMailingAddressChange={handleSameAsMailingAddressChange}
      />
    </PaymentPrefLayoutForm>
  );
};
